import {
  Badge,
  Button,
  Card,
  Checkbox,
  Grid,
  Loader,
  SegmentedControl,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconExternalLink, IconPlus, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import Regex from '../../../constants/Regex';
import isInvalidCronExpression from '../../../helpers/cronValidator';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import NextSyncLabel from '../../NextSyncLabel';
import Spinner from '../../Spinner';
import CustomFieldSelect from '../CustomFieldSelect';
import SearchableCombobox from '../../SearchableCombobox';

const POLL_INTERVAL_MS = 5000;
// Cap background polling so a run stuck in a non-terminal state (e.g. a worker
// dies mid-`running`) doesn't poll forever while the tab is open. ~10 minutes.
const MAX_POLL_ATTEMPTS = 120;
const STATUS_COLOR = { succeeded: 'green', failed: 'red', running: 'blue' };
const TERMINAL_STATUSES = ['succeeded', 'failed'];

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '—');

const formatDuration = (startValue, endValue) => {
  if (!startValue || !endValue) return '—';
  const seconds = Math.max(0, Math.round((new Date(endValue) - new Date(startValue)) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

const SettingsTabProductSync = ({ domain, handleChange }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();

  const [testResult, setTestResult] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [customFields, setCustomFields] = useState([]);
  const [error, setError] = useState();
  const [syncRuns, setSyncRuns] = useState([]);
  const [lastSuccessful, setLastSuccessful] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const syncRefreshTimeout = useRef(null);

  const customFieldsWithoutCategories = useMemo(
    () => customFields.filter((field) => !field.startsWith('category_level_')),
    [customFields],
  );

  const comboboxOptions = useMemo(
    () => customFieldsWithoutCategories.map((field) => ({ value: field, label: field })),
    [customFieldsWithoutCategories],
  );

  const customFieldsAvailableToChoose = useMemo(
    () => [
      ...new Set([
        ...customFields.filter((field) => field.startsWith('category_level_')),
        ...(domain.specificProductCustomFields?.filter(Boolean) || customFields),
      ]),
    ],
    [customFields, domain.specificProductCustomFields],
  );

  useEffect(() => {
    setIsLoading(true);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`)
      .then(({ customFields }) => {
        if (customFields?.product?.length) {
          setCustomFields(customFields.product.map((field) => field.key));
        }
      })
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [accountId, domainId, fetch]);

  // `silent` skips the spinner so background polling doesn't flicker the table.
  const loadSyncRuns = useCallback(
    ({ silent = false } = {}) => {
      if (!silent) setHistoryLoading(true);
      return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/productSyncRuns`)
        .then(({ runs, lastSuccessful }) => {
          setSyncRuns(runs || []);
          setLastSuccessful(lastSuccessful || null);
          setHistoryError(null);
        })
        // Surface failures (e.g. endpoint missing/5xx) instead of masquerading
        // as an empty history. useFetch rejects with the parsed error. Skip on
        // silent background polls so a transient blip doesn't flicker the table
        // from the data the user is watching to an error and back.
        .catch((e) => {
          if (!silent) setHistoryError(e);
        })
        .finally(() => setHistoryLoading(false));
    },
    [accountId, domainId, fetch],
  );

  useEffect(() => {
    loadSyncRuns();
  }, [loadSyncRuns]);

  // While a sync is in progress the run row keeps changing on the backend, so
  // poll quietly until every run reaches a terminal state. Keying off "not
  // terminal" (rather than === 'running') means any non-terminal status the API
  // might add later still triggers polling.
  const hasActiveRun = useMemo(() => syncRuns.some((run) => !TERMINAL_STATUSES.includes(run.status)), [syncRuns]);

  useEffect(() => {
    if (!hasActiveRun) return undefined;
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      loadSyncRuns({ silent: true });
      if (attempts >= MAX_POLL_ATTEMPTS) clearInterval(interval);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [hasActiveRun, loadSyncRuns]);

  // Clear the post-"Sync Now" refresh timeout if we unmount before it fires.
  useEffect(() => () => clearTimeout(syncRefreshTimeout.current), []);

  useEffect(() => {
    setTestResult({});
  }, [domain.productSyncApiUrl, domain.productSyncType]);

  let productSyncApiResponse;
  if (testResult?.testProductSync?.response) {
    productSyncApiResponse = (
      <>
        <span>API Response: </span>
        <code>{JSON.stringify(testResult?.testProductSync?.response)}</code>
      </>
    );
  }

  const testUrl = (suffix, url) => {
    setTestResult({ [suffix]: {} });
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/${suffix}`, {
      method: 'POST',
      body: JSON.stringify({ url }),
    })
      .then((result) => {
        setTestResult({ [suffix]: { response: result.response } });
      })
      .catch((e) => setTestResult({ [suffix]: { error: e.message } }));
  };

  const syncProductsNow = () => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/scheduleProductSync`, {
      method: 'POST',
      body: JSON.stringify({ productSyncType: domain.productSyncType }),
    })
      .then(() => {
        notifications.show({
          title: 'Sync Scheduled',
          message: 'Sync has been scheduled successfully.',
          color: 'success',
        });
        // Refresh now and again shortly after, to catch the run once the worker
        // picks it up; once it shows as 'running' the polling effect takes over.
        loadSyncRuns({ silent: true });
        clearTimeout(syncRefreshTimeout.current);
        syncRefreshTimeout.current = setTimeout(() => loadSyncRuns({ silent: true }), 3000);
      })
      .catch((e) => {
        notifications.show({
          title: 'Catalog Sync Error',
          message: `We were unable to schedule the catalog sync due to the following error: ${e.message}.`,
          color: 'danger',
        });
      });
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Catalog Sync Type
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={domain.productSyncType ?? ''}
              onChange={(value) => handleChange({ target: { name: 'productSyncType', value } })}
              data={[
                { label: 'API', value: 'api' },
                { label: 'Facebook CSV Feed', value: 'fbCsvFeed' },
                { label: 'Facebook XML Feed', value: 'fbXmlFeed' },
              ]}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}></Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <TextInput
                name="productSyncApiUrl"
                type="text"
                placeholder="https://myawesomeshop.com"
                value={domain.productSyncApiUrl || ''}
                error={testResult?.testProductSync?.error}
                onChange={handleChange}
                style={{ flex: 1 }}
              />
              <Button
                disabled={!domain.productSyncApiUrl}
                variant="default"
                onClick={
                  domain.productSyncType === 'api' ? () => testUrl('testProductSync', domain.productSyncApiUrl) : null
                }
                component={domain.productSyncType !== 'api' ? 'a' : 'button'}
                href={domain.productSyncType !== 'api' ? domain.productSyncApiUrl : undefined}
                rel="noopener noreferrer"
                target="_blank"
              >
                Test
              </Button>
              <Button disabled={!domain.productSyncApiUrl} onClick={() => syncProductsNow()}>
                Sync Now
              </Button>
            </div>
            <Text size="sm" c="dimmed">
              Implementing this API allow us to periodically sync your catalogue in case you fail to send us updates.
              You can find the spec{' '}
              <a
                href="https://releva.ai/documentation/sync-apis/periodical-product-sync/"
                rel="noopener noreferrer"
                target="_blank"
              >
                here <IconExternalLink size={14} />
              </a>
              .
            </Text>
            {productSyncApiResponse}
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}></Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Checkbox
              label="Skip products with no image"
              name="skipProductsWithoutImages"
              checked={domain.skipProductsWithoutImages}
              onChange={(e) =>
                handleChange({
                  target: {
                    name: 'skipProductsWithoutImages',
                    value: e.currentTarget.checked,
                  },
                })
              }
            />
          </Grid.Col>
        </Grid>
        {!domain.skipProductsWithoutImages && (
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Text size="sm" fw={500}>
                URL to Default Product Picture
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <TextInput
                name="defaultProductImageUrl"
                autoComplete="nope"
                type="text"
                value={domain.defaultProductImageUrl || ''}
                placeholder={`e.g. ${domain.url}`}
                error={
                  domain.defaultProductImageUrl &&
                  !domain.defaultProductImageUrl.match(Regex.urlRegex) &&
                  !domain.defaultProductImageUrl.match('{{(.+?)}}')
                    ? 'Valid URL or empty field is required.'
                    : null
                }
                onChange={handleChange}
              />
            </Grid.Col>
          </Grid>
        )}
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Catalog Sync Schedule
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="productSyncSchedule"
              type="text"
              placeholder="0 7 * * *"
              value={domain.productSyncSchedule || ''}
              error={
                isInvalidCronExpression(domain.productSyncSchedule) ? 'This is not a valid cron expression.' : null
              }
              onChange={handleChange}
            />
            <Text size="sm" c="dimmed">
              This controls how often a full product sync occurs between your shop and Releva. This needs to be a{' '}
              <a href="https://crontab.guru/" rel="noreferrer" target="_blank">
                valid cron expression <IconExternalLink size={14} />
              </a>
              . It will be evaluated under the UTC timezone.
            </Text>
            <NextSyncLabel schedule={domain.productSyncSchedule} />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Product Custom Fields To Sync
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={domain.specificProductCustomFields ? 'specific' : 'all'}
              onChange={(value) =>
                handleChange({
                  target: {
                    name: 'specificProductCustomFields',
                    value: value === 'all' ? null : [],
                  },
                })
              }
              data={[
                { label: 'All', value: 'all' },
                { label: 'Specific', value: 'specific' },
              ]}
            />
          </Grid.Col>
        </Grid>

        {domain.specificProductCustomFields && (
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }}></Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              {error ? (
                <Grid>
                  <ErrorMessage message={error.message}></ErrorMessage>
                </Grid>
              ) : (
                <Grid>
                  {domain.specificProductCustomFields.map((customField, index) => (
                    <Grid.Col span={12} key={customField || index}>
                      <Grid align="center">
                        <Grid.Col span={{ base: 12, sm: 10 }}>
                          <SearchableCombobox
                            options={comboboxOptions}
                            value={customField || null}
                            onChange={(value) => {
                              const changedCustomFields = [...domain.specificProductCustomFields];
                              changedCustomFields[index] = value || '';

                              handleChange({
                                target: {
                                  name: 'specificProductCustomFields',
                                  value: changedCustomFields,
                                },
                              });
                            }}
                            placeholder="Please select a field"
                            clearable
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 2 }} style={{ textAlign: 'center' }}>
                          <Button
                            onClick={() => {
                              const newFilters = domain.specificProductCustomFields.filter((f, i) => i !== index);
                              handleChange({
                                target: {
                                  name: 'specificProductCustomFields',
                                  value: newFilters,
                                },
                              });
                            }}
                            color="red"
                            size="sm"
                            p="xs"
                          >
                            <IconX size={14} />
                          </Button>
                        </Grid.Col>
                      </Grid>
                    </Grid.Col>
                  ))}

                  <Grid.Col span={12}>
                    <Button
                      fullWidth
                      onClick={() => {
                        const prevFilters = domain.specificProductCustomFields || [];
                        const e = {
                          target: {
                            name: 'specificProductCustomFields',
                            value: [...prevFilters, ''],
                          },
                        };
                        handleChange(e);
                      }}
                      variant="default"
                      size="sm"
                      className="pt-2 pb-2"
                    >
                      <IconPlus size={14} /> Add New Custom Field
                    </Button>
                  </Grid.Col>
                </Grid>
              )}
            </Grid.Col>
          </Grid>
        )}

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              "Brand" field
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <CustomFieldSelect
              fieldName="productsBrandFieldName"
              customFields={customFieldsAvailableToChoose}
              selectedValue={domain.productsBrandFieldName}
              handleChange={handleChange}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              "Color" field
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <CustomFieldSelect
              fieldName="productsColorFieldName"
              customFields={customFieldsAvailableToChoose}
              selectedValue={domain.productsColorFieldName}
              handleChange={handleChange}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              "Size" field
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <CustomFieldSelect
              fieldName="productsSizeFieldName"
              customFields={customFieldsAvailableToChoose}
              selectedValue={domain.productsSizeFieldName}
              handleChange={handleChange}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md" mt="lg">
          <Grid.Col span={12}>
            <Text size="md" fw={600} mb="xs">
              Sync History
            </Text>
          </Grid.Col>
          <Grid.Col span={12}>
            {historyLoading ? (
              <Loader size="sm" />
            ) : historyError ? (
              <ErrorMessage message={historyError.message || 'Failed to load sync history.'} />
            ) : (
              <>
                <Text size="sm" mb="md">
                  Last successful sync:{' '}
                  {lastSuccessful ? (
                    <strong>
                      {formatDateTime(lastSuccessful.finishedAt)} · {lastSuccessful.productsSynced ?? '—'} products ·{' '}
                      {formatDuration(lastSuccessful.syncStartedAt, lastSuccessful.finishedAt)}
                      {lastSuccessful.productsInvalid > 0 ? ` · ${lastSuccessful.productsInvalid} invalid` : null}
                    </strong>
                  ) : (
                    <strong>Never</strong>
                  )}
                </Text>
                {syncRuns.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No sync attempts recorded yet.
                  </Text>
                ) : (
                  <Table striped highlightOnHover withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Started</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Products</Table.Th>
                        <Tooltip
                          label="Products that failed catalog validation. On API syncs they were skipped and not written to the catalog. On feed syncs they were still written — the count shows what would be skipped."
                          multiline
                          w={420}
                          withArrow
                        >
                          <Table.Th>Invalid</Table.Th>
                        </Tooltip>
                        <Table.Th>Page reached</Table.Th>
                        <Table.Th>Attempts</Table.Th>
                        <Table.Th>Duration</Table.Th>
                        <Table.Th>Error</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {syncRuns.map((run) => (
                        <Table.Tr key={run.id}>
                          <Table.Td>{formatDateTime(run.syncStartedAt || run.createdAt)}</Table.Td>
                          <Table.Td>
                            <Badge color={STATUS_COLOR[run.status] || 'gray'} variant="light">
                              {run.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td>{run.productsSynced ?? '—'}</Table.Td>
                          <Table.Td>
                            {run.productsInvalid > 0 ? (
                              <Text size="sm" c="orange" fw={500}>
                                {run.productsInvalid}
                              </Text>
                            ) : (
                              (run.productsInvalid ?? '—')
                            )}
                          </Table.Td>
                          <Table.Td>{run.syncType === 'api' ? (run.lastPage ?? '—') : '—'}</Table.Td>
                          <Table.Td>{run.attemptCount}</Table.Td>
                          <Table.Td>{formatDuration(run.syncStartedAt, run.finishedAt)}</Table.Td>
                          <Table.Td style={{ maxWidth: 280 }}>
                            {run.error ? (
                              <Tooltip label={run.error} multiline w={420} withArrow>
                                <Text size="sm" c="red" truncate>
                                  {run.error}
                                </Text>
                              </Tooltip>
                            ) : (
                              '—'
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </>
            )}
          </Grid.Col>
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default SettingsTabProductSync;
